import { useEffect, useMemo, useState } from "react";
import { GAMES, GAMES_BY_TIME } from "../data/games";
import {
  fetchGameResultForLive,
  fetchGameResultForYear,
  getNowIST,
} from "./results";

/** A ticking IST clock, shared by the header and the freshness timestamps. */
export function useNowIST() {
  const [nowIST, setNowIST] = useState(getNowIST);

  useEffect(() => {
    const id = setInterval(() => setNowIST(getNowIST()), 1000);
    return () => clearInterval(id);
  }, []);

  return nowIST;
}

/** Today + yesterday numbers for every game. */
export function useLiveResults() {
  const [gameResults, setGameResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadLive() {
      try {
        setIsLoading(true);
        setError("");
        const results = await Promise.all(
          GAMES.map(async (g) => {
            try {
              const r = await fetchGameResultForLive(g);
              return { game: g, ...r };
            } catch (err) {
              console.error(`Failed to load ${g.name}:`, err);
              return {
                game: g,
                yesterday: null,
                today: null,
                shouldShowToday: false,
                monthDataByMonth: {},
              };
            }
          })
        );
        if (!cancelled) setGameResults(results);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            "There was a problem loading the results. Please try again in some time."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadLive();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () =>
      GAMES_BY_TIME.map((g) => {
        const r = gameResults.find((x) => x.game.key === g.key);
        if (!r) {
          return {
            game: g,
            yesterday: null,
            today: null,
            shouldShowToday: false,
          };
        }
        return {
          game: g,
          yesterday: r.yesterday,
          today: r.today,
          shouldShowToday: r.shouldShowToday,
        };
      }),
    [gameResults]
  );

  return { rows, isLoading, error };
}

/**
 * Full-year chart data.
 *
 * `games` defaults to every game (the chart page); a single-game page passes
 * just its own config so it only pulls the one sheet it needs.
 */
export function useChartResults(selectedYear, games = GAMES_BY_TIME) {
  const [chartResults, setChartResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const gameKeys = games.map((g) => g.key).join(",");

  useEffect(() => {
    let cancelled = false;

    async function loadChart() {
      try {
        setIsLoading(true);
        setError("");
        const results = await Promise.all(
          games.map(async (g) => {
            try {
              const r = await fetchGameResultForYear(g, selectedYear);
              return { game: g, ...r, yearLoaded: selectedYear };
            } catch (err) {
              console.error(`Failed to load chart for ${g.name}:`, err);
              return {
                game: g,
                monthDataByMonth: {},
                yearLoaded: selectedYear,
              };
            }
          })
        );
        if (!cancelled) setChartResults(results);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            "There was a problem loading the chart data. Please try again in some time."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadChart();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, gameKeys]);

  return { chartResults, isLoading, error };
}
